import { useCallback, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { parseApiError } from "@/lib/error-handling";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import { confirmAsync, useToast } from "@/lib/ui";
import {
  useAdminTagCreate,
  useAdminTagDelete,
  useAdminTagUpdate,
  useAdminTagsList,
  type AdminTagListItem,
} from "@/lib/admin-api";

const TAGS_PAGE_SIZE = 10;

export function TagsList() {
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");
  const [pageFromUrl, setPageFromUrl] = useListPageParam();

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: TAGS_PAGE_SIZE,
    initialSortField: "name",
    initialSortOrder: 1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * TAGS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  const [tagDialog, setTagDialog] = useState(false);
  const [tag, setTag] = useState<Partial<AdminTagListItem>>({ name: "" });
  const [submitted, setSubmitted] = useState(false);
  const { toastRef, showToast } = useToast();

  const tagsQuery = useAdminTagsList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filterText: globalFilter,
  });
  const tags = tagsQuery.data?.data ?? [];
  const totalRecords = tagsQuery.data?.pagination.total ?? 0;
  const loading = tagsQuery.isFetching;

  const tagCreate = useAdminTagCreate();
  const tagUpdate = useAdminTagUpdate();
  const tagDelete = useAdminTagDelete();

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    resetToFirstPage();
  };

  const openNew = () => {
    setTag({ name: "" });
    setSubmitted(false);
    setTagDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setTagDialog(false);
  };

  const saveTag = async () => {
    setSubmitted(true);

    if (tag.name?.trim()) {
      try {
        if (tag.id) {
          await tagUpdate.mutateAsync({
            params: { path: { id: tag.id } },
            body: { name: tag.name },
          });
          showToast({
            severity: "success",
            summary: "Successful",
            detail: "Tag Updated",
            life: 3000,
          });
        } else {
          await tagCreate.mutateAsync({
            body: {
              name: tag.name,
            },
          });
          showToast({
            severity: "success",
            summary: "Successful",
            detail: "Tag Created",
            life: 3000,
          });
        }

        setTagDialog(false);
        setTag({ name: "" });
      } catch (error: unknown) {
        const { message } = parseApiError(error);
        const detail = message || "Failed to save tag";
        showToast({ severity: "error", summary: "Error", detail, life: 3000 });
      }
    }
  };

  const editTag = (tag: AdminTagListItem) => {
    setTag({ ...tag });
    setTagDialog(true);
  };

  const confirmDeleteTag = async (tag: AdminTagListItem) => {
    const confirmed = await confirmAsync({
      message: `Are you sure you want to delete "${tag.name}"?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      defaultFocus: "reject",
    });
    if (!confirmed) return;
    await deleteTag(tag);
  };

  const deleteTag = async (tag: AdminTagListItem) => {
    try {
      await tagDelete.mutateAsync({
        params: { path: { id: tag.id } },
      });

      showToast({
        severity: "success",
        summary: "Successful",
        detail: "Tag Deleted",
        life: 3000,
      });
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      const detail = message || "Failed to delete tag";
      showToast({ severity: "error", summary: "Error", detail, life: 3000 });
    }
  };

  const actionBodyTemplate = (rowData: AdminTagListItem) => {
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={() => editTag(rowData)} />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-danger p-button-sm"
          onClick={() => {
            void confirmDeleteTag(rowData);
          }}
        />
      </div>
    );
  };

  const tagDialogFooter = (
    <>
      <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          void saveTag();
        }}
      />
    </>
  );

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <ConfirmDialog />
      
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">Manage Tags</h1>
        <div className="flex gap-2 flex-column md:flex-row">
          <span className="p-input-icon-left w-full md:w-auto">
            <i className="pi pi-search" />
            <InputText value={globalFilter} onChange={onFilter} placeholder="Search tags..." className="w-full" />
          </span>
          <Button label="New Tag" icon="pi pi-plus" onClick={openNew} className="w-full md:w-auto p-button-outlined" />
        </div>
      </div>

      <DataTable
        value={tags}
        lazy
        paginator
        rows={lazyParams.rows}
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        loading={loading}
        responsiveLayout="scroll"
        emptyMessage="No tags found"
        className="text-sm"
      >
        <Column field="name" header="Name" sortable style={{ minWidth: "200px" }} />
        <Column body={actionBodyTemplate} header="Actions" style={{ minWidth: "100px" }} />
      </DataTable>

      <Dialog visible={tagDialog} style={{ width: "450px" }} header="Tag Details" modal className="p-fluid" footer={tagDialogFooter} onHide={hideDialog}>
        <div className="field">
          <label htmlFor="name">Name</label>
          <InputText id="name" value={tag.name} onChange={(e) => setTag({ ...tag, name: e.target.value })} required autoFocus className={submitted && !tag.name ? "p-invalid" : ""} />
          {submitted && !tag.name && <small className="p-error">Name is required.</small>}
        </div>
      </Dialog>
    </div>
  );
}
